import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Select, Card } from "../components/ui";

const SEVERITA_LABELS = {
  mancante: {
    label: "Mancante",
    color: "bg-red-100 text-red-700 border-red-200",
  },
  scaduta: {
    label: "Scaduta",
    color: "bg-orange-100 text-orange-700 border-orange-200",
  },
};

function SegnalazioniObbligatoriPage() {
  const { apiRequest, user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [gaps, setGaps] = useState([]);
  const [summary, setSummary] = useState({
    totale: 0,
    mancanti: 0,
    scadute: 0,
  });
  const [rsaList, setRsaList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtroRsa, setFiltroRsa] = useState("");
  const [filtroSeverita, setFiltroSeverita] = useState("");

  const loadGaps = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroRsa) params.set("rsaId", filtroRsa);
      if (filtroSeverita) params.set("severita", filtroSeverita);

      const data = await apiRequest(
        `/segnalazioni-obbligatori${params.toString() ? `?${params}` : ""}`,
      );
      setGaps(data.value || []);
      setSummary(data.summary || { totale: 0, mancanti: 0, scadute: 0 });
    } catch (error) {
      showToast("Errore nel caricamento delle segnalazioni", "error");
    } finally {
      setLoading(false);
    }
  }, [apiRequest, filtroRsa, filtroSeverita, showToast]);

  useEffect(() => {
    apiRequest("/rsa")
      .then((d) => setRsaList(d.value || []))
      .catch(() => {});
  }, [apiRequest]);

  useEffect(() => {
    loadGaps();
  }, [loadGaps]);

  const handleCreaScadenza = (gap) => {
    const params = new URLSearchParams({
      rsaId: gap.rsaId,
      tipologiaId: gap.tipologiaId,
    });
    navigate(`/scadenze/nuova?${params}`);
  };

  const rsaOptions = rsaList.map((r) => ({
    value: r.id || r.phc_rsaid,
    label: r.nome || r.phc_name,
  }));

  const severitaOptions = [
    { value: "mancante", label: "Mancante" },
    { value: "scaduta", label: "Scaduta" },
  ];

  // Raggruppa per RSA
  const gapsByRsa = gaps.reduce((acc, gap) => {
    const key = gap.rsaId;
    if (!acc[key]) acc[key] = { rsaNome: gap.rsaNome, items: [] };
    acc[key].items.push(gap);
    return acc;
  }, {});

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Obblighi Normativi</h1>
        <p className="text-gray-500 mt-1">
          Interventi obbligatori per legge privi di una scadenza attiva
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-l-4 border-gray-300">
          <div className="text-3xl font-bold text-gray-900">
            {summary.totale}
          </div>
          <div className="text-sm text-gray-500 mt-1">Totale gap</div>
        </Card>
        <Card className="p-4 border-l-4 border-red-400">
          <div className="text-3xl font-bold text-red-600">
            {summary.mancanti}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Mancanti — nessuna scadenza mai creata
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-orange-400">
          <div className="text-3xl font-bold text-orange-600">
            {summary.scadute}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Scadute — ultima scadenza non rinnovata
          </div>
        </Card>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="w-64">
          <Select
            label=""
            name="filtroRsa"
            value={filtroRsa}
            onChange={(e) => setFiltroRsa(e.target.value)}
            options={rsaOptions}
            placeholder="Tutte le RSA"
          />
        </div>
        <div className="w-48">
          <Select
            label=""
            name="filtroSeverita"
            value={filtroSeverita}
            onChange={(e) => setFiltroSeverita(e.target.value)}
            options={severitaOptions}
            placeholder="Tutti i tipi"
          />
        </div>
        <Button variant="ghost" onClick={loadGaps} disabled={loading}>
          Aggiorna
        </Button>
      </div>

      {/* Contenuto */}
      {loading ? (
        <div className="text-center text-gray-500 py-12">Caricamento...</div>
      ) : gaps.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="text-4xl mb-3">✅</div>
          <div className="text-lg font-medium text-gray-700">
            Tutti gli obblighi normativi sono coperti
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Ogni tipologia obbligatoria ha una scadenza attiva nelle RSA
            configurate
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(gapsByRsa).map(([rsaId, { rsaNome, items }]) => (
            <Card key={rsaId} className="overflow-hidden">
              {/* Header RSA */}
              <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🏥</span>
                  <span className="font-semibold text-gray-800">{rsaNome}</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                    {items.length} gap
                  </span>
                </div>
              </div>

              {/* Righe gap */}
              <div className="divide-y">
                {items.map((gap) => {
                  const sev = SEVERITA_LABELS[gap.severita];
                  return (
                    <div
                      key={gap.obbligoId}
                      className="px-4 py-3 flex items-center gap-4"
                    >
                      {/* Badge severità */}
                      <span
                        className={`shrink-0 px-2 py-1 text-xs font-medium rounded-full border ${sev.color}`}
                      >
                        {sev.label}
                      </span>

                      {/* Info tipologia */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {gap.tipologiaNome}
                        </div>
                        {gap.tipologiaRiferimentoNormativo && (
                          <div className="text-xs text-gray-500 truncate mt-0.5">
                            {gap.tipologiaRiferimentoNormativo}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                          {/* Scadenza */}
                          {gap.severita === "scaduta" &&
                          gap.dataUltimaScadenza ? (
                            <span className="text-xs text-orange-600">
                              📅 Scaduta il{" "}
                              {new Date(
                                gap.dataUltimaScadenza,
                              ).toLocaleDateString("it-IT")}
                            </span>
                          ) : (
                            <span className="text-xs text-red-500">
                              📅 Nessuna scadenza programmata
                            </span>
                          )}
                          {/* Ultimo intervento */}
                          {gap.dataUltimoIntervento ? (
                            <span className="text-xs text-gray-500">
                              🔧 Ultimo intervento:{" "}
                              {new Date(
                                gap.dataUltimoIntervento,
                              ).toLocaleDateString("it-IT")}
                            </span>
                          ) : (
                            <span className="text-xs text-red-400 font-medium">
                              ⚠️ Nessun intervento registrato
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Fornitore assegnato */}
                      <div className="shrink-0 min-w-[160px] max-w-[220px]">
                        {gap.fornitoriAssegnati?.length > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            {gap.fornitoriAssegnati.map((f) => (
                              <span
                                key={f.id}
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 truncate"
                                title={f.nome}
                              >
                                🏢 {f.nome}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                            ⚠️ Nessun fornitore
                          </span>
                        )}
                      </div>

                      {/* Azione */}
                      <Button
                        variant="primary"
                        onClick={() => handleCreaScadenza(gap)}
                        className="shrink-0 text-sm"
                      >
                        + Crea scadenza
                      </Button>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default SegnalazioniObbligatoriPage;
